import React, { useEffect } from "react";
import {
  Badge,
  Box,
  Link,
  Typography,
  useScrollTrigger,
} from "@material-ui/core";
import clsx from "clsx";

import { MobileWishlistAvailabilityConnectorProps } from "./container";
import "./styles.scss";

import { RouteComponentProps } from "react-router-dom";
import { config } from "../../../../api/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  AvailabilityFilter,
  AvailabilityList,
  AvailabilityNoResults,
} from "../../../availability/components";
import { ConnectedAvailabilityClusterMap } from "../../../availability/components/AvailabilityClusterMap/container";
import { HotelAvailabilityCallState } from "../../../availability/reducer/state";
import { RewardsAccountSelection } from "../../../rewards/components";
import { COUNT_HEADING_TEXT } from "../../../availability/components/AvailabilityList/textConstants";
import {
  ActionLink,
  DatePickerButton,
  Icon,
  IconName,
  WishlistHeartIconEntry,
  wishlistManagementModalActions,
} from "halifax";
import { MobileCalendarPickerModal } from "../../../search/components/MobileHotelSearchControlV3/components/MobileCalendarPickerModal";
import { OccupancySelection } from "../../../search/components/MobileHotelSearchControl/components";
import { TravelWalletDrawer } from "../../../travel-wallet/components";
import { useUpdateWishlistMutation, wishlistsApi } from "@capone/common";
import { Wishlist } from "@b2bportal/travel-wishlists-api";
import {
  FAVORITES_EDITED_LIST,
  FAVORITES_VIEWED_LIST,
  GuestsSelection,
} from "redmond";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ConnectedPreviewMap } from "../../../availability/components/AvailabilityClusterMap/components/PreviewMap/container";

export interface IMobileWishlistAvailabilityProps
  extends MobileWishlistAvailabilityConnectorProps,
    RouteComponentProps {
  onShowMap?: (open: boolean) => void;
  wishlist: Wishlist;
}

export const MobileWishlistAvailability = (
  props: IMobileWishlistAvailabilityProps
) => {
  const {
    history,
    onShowMap,
    wishlist,
    lodgings,
    isFilteredHotelAvailabilityLodgingsEmpty,
    hotelAvailabilityCallState,
    fromDate,
    untilDate,
    walletItemCount,
    setOpenDatesModal,
  } = props;

  const dispatch = useDispatch();

  const hasWishlists = Boolean(
    useSelector(wishlistsApi.endpoints.getAllWishlists.select())?.data
      ?.wishlists?.length
  );

  const initialCallInProgress =
    hotelAvailabilityCallState ===
    HotelAvailabilityCallState.InitialSearchCallInProcess;

  const [showMap, setShowMap] = React.useState(false);
  const [openCalendarModal, setOpenCalendarModal] = React.useState(false);
  const [travelWalletDrawerOpen, setTravelWalletDrawerOpen] =
    React.useState<boolean>(false);

  useEffect(() => {
    onShowMap?.(showMap);
    if (showMap) {
      // removes medallia from the fixed bottom that was covering pricing (only on the enlarged map)
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "none";
      }
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [showMap]);

  const renderMap = (onClickPreview: () => void) => {
    return (
      <ConnectedPreviewMap
        onClick={() => onClickPreview()}
        style={{ height: "130px" }}
      />
    );
  };

  const renderFullHeightMap = () => {
    return (
      <Box
        position="relative"
        height="calc(100%)"
        top="-50px"
        width="auto"
        zIndex={2}
      >
        <Box
          position="absolute"
          top="20px"
          left="50%"
          width="100%"
          display="flex"
          justifyContent="space-between"
          zIndex={3}
          style={{
            transform: "translate(-50%, 0%)",
          }}
        >
          <Box
            width="34px"
            height="34px"
            justifyContent="center"
            display="flex"
            alignItems="center"
            bgcolor="white"
            marginLeft="10px"
            border="0.5px solid var(--grey-5)"
            borderRadius="4px"
          >
            <Link
              aria-label="back"
              component="button"
              onClick={() => setShowMap(false)}
            >
              <FontAwesomeIcon color="var(--blue-9)" icon={faChevronLeft} />
            </Link>
          </Box>
          <AvailabilityFilter />
        </Box>
        <ConnectedAvailabilityClusterMap isWishlistMode={true} />
      </Box>
    );
  };

  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });

  const onClickPreview = () => {
    setShowMap(true);
    window.scrollTo(0, 0);
  };

  const handleOpenCalendarModal = (open: boolean) => {
    setOpenCalendarModal(open);
    setOpenDatesModal(open);
  };

  const [updateWishlist] = useUpdateWishlistMutation();

  useEffect(() => {
    if (lodgings.length > 0) {
      const unavailableProperties = lodgings.filter(
        (lodging) => !lodging.available
      );
      if (wishlist.id.value && fromDate && untilDate) {
        const startOfToday = dayjs().startOf("day");
        const advance = dayjs(fromDate).diff(startOfToday, "day");
        const los = dayjs(untilDate).diff(dayjs(fromDate), "day");
        trackEvent({
          eventName: FAVORITES_VIEWED_LIST,
          properties: {
            favorites_id: wishlist.id.value,
            advance,
            los,
            number_of_properties: lodgings.length,
            unavailable_properties: unavailableProperties.length,
          },
        });
      }
    }
  }, [lodgings]);

  const handleUpdateDates = (from: Date | null, until: Date | null) => {
    const { name, adults, children, petsIncluded } = wishlist.wishlistData;
    updateWishlist({
      wishlistId: { value: wishlist.id.value ?? "" },
      wishlistData: {
        name: name || "List",
        checkInDate: dayjs(from).format("YYYY-MM-DD"),
        checkOutDate: dayjs(until).format("YYYY-MM-DD"),
        adults,
        children,
        petsIncluded,
      },
    });
    trackEvent({
      eventName: FAVORITES_EDITED_LIST,
      properties: {
        favorites_id: wishlist.id.value,
        action: "edit_dates",
      },
    });
  };

  const handleUpdateOccupany = (guests: GuestsSelection) => {
    updateWishlist({
      wishlistId: { value: wishlist.id.value ?? "" },
      wishlistData: {
        name: wishlist.wishlistData?.name || "List",
        checkInDate: wishlist.wishlistData?.checkInDate,
        checkOutDate: wishlist.wishlistData?.checkOutDate,
        adults: guests.adults,
        children: guests.children.length,
        petsIncluded: Boolean(guests.pets && guests.pets > 0),
      },
    });
    trackEvent({
      eventName: FAVORITES_EDITED_LIST,
      properties: {
        favorites_id: wishlist.id.value,
        action: "edit_guests",
      },
    });
  };

  const handleBackButton = () => {
    // Pushes the navigation to the next event loop tick to avoid React unmounting issues
    setTimeout(() => {
      history.goBack();
    }, 0);
  };

  return (
    <>
      <Box className={clsx("mobile-wishlist-availability-page", config.TENANT)}>
        {!showMap && (
          <>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              className={clsx("mobile-location-search-contents", {
                "global-mobile-nav": true,
                scrolled: scrollTrigger,
              })}
            >
              <Box display="flex" flexBasis="0" paddingLeft="20px">
                <ActionLink
                  className={clsx("go-back-button")}
                  onClick={handleBackButton}
                  content={<FontAwesomeIcon icon={faChevronLeft} />}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="space-evenly"
                width="100%"
                padding="8px 0px 8px 20px"
              >
                <DatePickerButton
                  startDate={fromDate}
                  endDate={untilDate}
                  renderCalendarPopup={() => (
                    <MobileCalendarPickerModal
                      openCalendarModal={openCalendarModal}
                      setOpenCalendarModal={handleOpenCalendarModal}
                      fullScreen
                      showPricing={false}
                      isWishlistMode={true}
                      onComplete={handleUpdateDates}
                    />
                  )}
                  onClick={() => handleOpenCalendarModal(true)}
                  dateFormat={"MMM D"}
                  variant="minimal"
                />

                <OccupancySelection
                  hideIcon={true}
                  showClosePassengerModalButton
                  showPets={true}
                  onClickApply={handleUpdateOccupany}
                  isWishlistMode={true}
                />
              </Box>
              <Box display="flex" flexBasis="0">
                <Box
                  paddingLeft="20px"
                  paddingRight="10px"
                  display="flex"
                  alignItems="center"
                  mt="2px"
                >
                  <WishlistHeartIconEntry
                    hasWishlists={hasWishlists}
                    onClick={() =>
                      dispatch(wishlistManagementModalActions.openModal({}))
                    }
                  />
                </Box>
                <Box paddingRight="20px" paddingLeft="10px">
                  <ActionLink
                    onClick={() => setTravelWalletDrawerOpen(true)}
                    content={
                      <Badge
                        className="wallet-corner-notification"
                        badgeContent={walletItemCount ? "" : undefined}
                      >
                        <Icon name={IconName.TravelWalletIcon} />
                      </Badge>
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Box
              className={clsx(
                "mobile-rewards-account-contents",
                "global-mobile-nav"
              )}
            >
              <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
            </Box>
            {!isFilteredHotelAvailabilityLodgingsEmpty && (
              <Box className="mobile-lodging-availability-contents">
                {!!lodgings && lodgings.length > 0 && (
                  <Box padding="20px">
                    <Typography style={{ fontSize: "14px" }}>
                      {COUNT_HEADING_TEXT(
                        lodgings.filter(
                          (curr) =>
                            typeof curr.available === "undefined" ||
                            curr.available
                        ).length,
                        "",
                        false,
                        false,
                        false,
                        true,
                        false,
                        [],
                        true
                      )}
                    </Typography>
                  </Box>
                )}
                {!!lodgings && lodgings.length > 0 && (
                  <Box
                    className={clsx(
                      "mobile-availability-map-preview",
                      "enhanced-hotel-filters"
                    )}
                  >
                    {renderMap(onClickPreview)}
                  </Box>
                )}
                {!isFilteredHotelAvailabilityLodgingsEmpty &&
                  !initialCallInProgress && (
                    <Box className="mobile-availability-list-wrapper">
                      <AvailabilityList isWishlistMode={true} />
                    </Box>
                  )}
              </Box>
            )}
          </>
        )}
        {showMap && renderFullHeightMap()}

        {isFilteredHotelAvailabilityLodgingsEmpty && (
          <Box className="mobile-availability-no-results-wrapper">
            <AvailabilityNoResults />
          </Box>
        )}
      </Box>
      <TravelWalletDrawer
        drawerOpen={travelWalletDrawerOpen}
        setDrawerOpen={setTravelWalletDrawerOpen}
      />
    </>
  );
};
